import type { CommonType, PostCommentParams, PostCommentResponse } from '@/server/types'
import { postCommentApi, postCommentFavoriteApi } from '@/server'
import { LOAD_STATE } from '@/shared/types'
import { COMMENT_ADD_KEY, COMMENT_CHILD_ADD_KEY, COMMENT_FAVORITE_KEY } from '@/shared/event'

export interface CommentFavorHandleParams {
  isChild: boolean
  firstLevelIndex: number
  secondLevelIndex: number
  item?: any
}

export function useComment() {
  const content = ref('')

  const state = ref(LOAD_STATE.INIT)
  // 发表评论
  async function submit(type: CommonType, params: PostCommentParams) {
    try {
      const res = await useLoadState({
        state,
        throwError: true,
        fn: () => postCommentApi(type, params),
      })

      const { data } = res || {}
      useMessage.success('评论成功')
      return data
    }
    catch (err) {
      const msg = getErrorMsg(err)
      useMessage.error(`评论失败，${msg || '评论失败，请稍后重试'}`)
    }
  }

  async function commentSubmit(data: {
    dataId: number
    dataType: CommonType
    commentId?: number
  }) {
    const { dataId, dataType } = data
    const validate = [
      [!dataId, '请刷新页面后重试'],
      [!content.value, '请输入要评论的内容'],
      [content.value.trim().length < 10, '评论内容不能少于10个字'],
    ] as const

    for (const [condition, message] of validate) {
      if (condition) {
        useMessage.warning(message)
        return
      }
    }

    const params: PostCommentParams = {
      id: dataId as number,
      content: content.value,
    }
    if (data.commentId)
      params.commentId = data.commentId

    return await submit(dataType, params)
  }

  return {
    content,
    state,
    commentSubmit,
  }
}

export function useCommentFavorite() {
  async function favorite(data: {
    dataType: CommonType
    isFavorite: boolean
    commentId: number
    firstLevelIndex: number
    secondLevelIndex: number
  }) {
    const text = data.isFavorite ? '' : '取消'
    try {
      await postCommentFavoriteApi(data.dataType, { commentId: data.commentId, isGood: data.isFavorite })
      useMessage.success(`${text}点赞成功`, { closable: true })
      useCommentFavoriteEvent().changeCommentFavorite(data)
    }
    catch (err) {
      const msg = getErrorMsg(err)
      useMessage.error(`操作失败，${msg || '操作失败，请稍后重试'}`)
      throw err
    }
  }

  return {
    favorite,
  }
}

export function useCommentEvent() {
  const bus = useEventBus<{ data: PostCommentResponse }>(COMMENT_ADD_KEY)

  function addComment(data: PostCommentResponse) {
    bus.emit({ data })
  }

  return {
    addComment,
    bus,
    on: bus.on,
    reset: bus.reset,
  }
}

export interface ChildCommentEvent {
  commentId: number
  firstLevelIndex: number
  secondLevelIndex: number
  toUserName: string
  data: PostCommentResponse
}
export function useChildCommentEvent() {
  const bus = useEventBus<ChildCommentEvent>(COMMENT_CHILD_ADD_KEY)

  function addComment(data: ChildCommentEvent) {
    bus.emit(data)
  }

  return {
    addComment,
    bus,
    on: bus.on,
    reset: bus.reset,
  }
}

export interface CommentFavoriteEvent {
  commentId: number
  isFavorite: boolean
  firstLevelIndex: number
  secondLevelIndex: number
}
export function useCommentFavoriteEvent() {
  const bus = useEventBus<CommentFavoriteEvent>(COMMENT_FAVORITE_KEY)
  function changeCommentFavorite(data: CommentFavoriteEvent) {
    bus.emit(data)
  }

  return {
    changeCommentFavorite,
    bus,
    on: bus.on,
    reset: bus.reset,
  }
}
